import { useEffect, useRef, useState, type ReactNode } from "react";
import type { AdminAiDatasetDetail } from "api/adminApi";
import { Badge, NUMERIC } from "pages/admin/ui";
import {
  CHAT_INPUT_WARN_CHARS,
  CHAT_SPLIT,
  agentContextOf,
  chatInputSize,
  chatWindowSize,
  diffTokens,
  fromBubbles,
  inputLines,
  speakerOf,
  toBubbles,
  userContent,
  type DiffToken,
} from "./aiFormat";

// 채팅 턴 상세: 대화 맥락 탭, AI 추천 vs 코치 최종, 말풍선 편집기, SFT 입력 크기 요약.
// 기획: "채팅 턴 상세 패널" (대화 · 에이전트 컨텍스트 · 모델 입력 원문 / 이번 턴 강조 / 차이 보기 / <split> 자동)

export type ChatContextTab = "dialogue" | "context" | "raw";

export const CHAT_CONTEXT_TABS: { key: ChatContextTab; label: string }[] = [
  { key: "dialogue", label: "대화" },
  { key: "context", label: "에이전트 컨텍스트" },
  { key: "raw", label: "모델 입력 원문" },
];

// {nickname} 은 가려진 값이라는 걸 알 수 있게 칩으로
function Masked({ text }: { text: string }) {
  const parts = text.split(/(\{nickname\})/);
  return (
    <>
      {parts.map((part, i) =>
        part === "{nickname}" ? (
          <span
            key={i}
            className="rounded bg-[#DCEFFA] px-1 font-mono text-[0.85em] text-[#0F55A7]"
          >
            {part}
          </span>
        ) : (
          part
        )
      )}
    </>
  );
}

function Bubble({
  who,
  text,
  onAccent,
}: {
  who: "patient" | "coach";
  text: string;
  onAccent?: boolean;
}) {
  return (
    <div
      className={`flex max-w-[85%] flex-col gap-0.5 ${
        who === "patient" ? "self-start" : "items-end self-end"
      }`}
    >
      <span className="px-1 text-[11px] text-slate-400">
        {who === "patient" ? "내담자" : "상담사"}
      </span>
      <div
        className={`whitespace-pre-wrap break-words rounded-2xl px-3 py-2 text-sm leading-relaxed ${
          who === "patient"
            ? "rounded-tl-sm border border-slate-200 bg-white text-slate-900"
            : "rounded-tr-sm bg-[#0F55A7] text-white"
        }`}
      >
        {onAccent ? text : <Masked text={text} />}
      </div>
    </div>
  );
}

/** 대화 맥락: 탭 셋. 대화 탭은 이번 턴 내담자 메시지를 묶어 강조하고, 그 아래에 정답 자리를 둔다. */
export function ChatContext({
  detail,
  tab,
  onTab,
}: {
  detail: AdminAiDatasetDetail;
  tab: ChatContextTab;
  onTab: (tab: ChatContextTab) => void;
}) {
  const lines = inputLines("chat", detail.input);
  const context = agentContextOf(detail.input);
  const hasDialogue = Array.isArray(detail.input?.dialogue);
  const windowSize = chatWindowSize(lines);
  const before = lines.slice(0, lines.length - windowSize);
  const turn = lines.slice(lines.length - windowSize);
  const boxRef = useRef<HTMLDivElement>(null);

  // 열리면 맨 아래(이번 턴)로
  useEffect(() => {
    const box = boxRef.current;
    if (box && tab === "dialogue") box.scrollTop = box.scrollHeight;
  }, [detail.id, tab]);

  const count = {
    dialogue: lines.length,
    context: context.length,
    raw: null,
  } as const;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <div className="flex gap-1 border-b border-slate-200" role="tablist">
        {CHAT_CONTEXT_TABS.map(({ key, label }) => (
          <button
            type="button"
            role="tab"
            aria-selected={tab === key}
            key={key}
            onClick={() => onTab(key)}
            className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-1.5 text-sm ${
              tab === key
                ? "border-[#0F55A7] font-semibold text-[#0F55A7]"
                : "border-transparent text-slate-500 hover:text-slate-900"
            }`}
          >
            {label}
            {count[key] != null && (
              <span
                className={`rounded-full bg-slate-100 px-1.5 text-[11px] text-slate-500 ${NUMERIC}`}
              >
                {count[key]}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === "dialogue" && (
        <div
          ref={boxRef}
          className="flex max-h-[60vh] min-h-0 flex-1 flex-col gap-2 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-3 lg:max-h-none"
        >
          {!hasDialogue && (
            <p className="rounded-md bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800">
              예전 형식 행이에요. 직전 대화 없이 내담자 메시지만 있어요.
            </p>
          )}
          {before.map((line, i) => {
            const { who, text } = speakerOf(line);
            return who ? (
              <Bubble key={i} who={who} text={text} />
            ) : (
              <p key={i} className="text-sm text-slate-600">
                {line}
              </p>
            );
          })}
          {turn.length > 0 && (
            <div className="flex flex-col gap-2 rounded-xl border-2 border-amber-400 bg-amber-50/60 p-2">
              <span className="text-[11px] font-bold text-amber-700">
                이번 턴 · 내담자 메시지 {turn.length}
              </span>
              {turn.map((line, i) => (
                <Bubble key={i} who="patient" text={speakerOf(line).text} />
              ))}
            </div>
          )}
          <span className="self-end rounded-2xl border-2 border-dashed border-[#0F55A7] px-3 py-1.5 text-xs text-[#0F55A7]">
            → 이 자리에 올 코치 답 = 오른쪽 학습 정답
          </span>
        </div>
      )}

      {tab === "context" &&
        (context.length === 0 ? (
          <p className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-500">
            {detail.aiOutput?.text
              ? "이 추천은 에이전트 기록이 없어요 (예전 LLM 추천)."
              : "이 턴엔 AI 추천이 없어 에이전트 컨텍스트가 없어요."}
          </p>
        ) : (
          <div className="flex max-h-[60vh] min-h-0 flex-1 flex-col gap-2 overflow-y-auto lg:max-h-none">
            {context.map((c, i) => (
              <details
                key={i}
                className="group rounded-lg border border-slate-200 bg-white"
              >
                <summary className="flex cursor-pointer flex-col gap-1 px-3 py-2">
                  <span className="flex items-center gap-2 text-xs">
                    <Badge tone="idle">{c.kind}</Badge>
                    <span className="truncate text-slate-400">{c.target}</span>
                    <span className={`ml-auto text-slate-400 ${NUMERIC}`}>
                      {c.excerpt.length.toLocaleString()}자
                    </span>
                  </span>
                  <span className="line-clamp-3 whitespace-pre-wrap text-sm text-slate-600 group-open:hidden">
                    {c.excerpt}
                  </span>
                </summary>
                <pre className="whitespace-pre-wrap break-words border-t border-slate-100 px-3 py-2 text-xs leading-relaxed text-slate-700">
                  {c.excerpt}
                </pre>
              </details>
            ))}
          </div>
        ))}

      {tab === "raw" && <RawInput text={userContent(detail)} />}
    </div>
  );
}

// 모델 입력 원문: SFT user 에 들어가는 텍스트 그대로
function RawInput({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const preRef = useRef<HTMLPreElement>(null);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // 클립보드를 못 쓰면 글을 선택해 둔다
      const pre = preRef.current;
      const selection = window.getSelection();
      if (pre && selection) {
        const range = document.createRange();
        range.selectNodeContents(pre);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  };
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-1.5">
      <div className="flex items-center justify-between text-xs text-slate-500">
        <span className={NUMERIC}>
          SFT user 그대로 · {text.length.toLocaleString()}자
        </span>
        <button
          type="button"
          onClick={copy}
          className="font-medium text-[#0F55A7] hover:underline"
        >
          {copied ? "복사했어요" : "복사"}
        </button>
      </div>
      <pre
        ref={preRef}
        className="max-h-[60vh] min-h-0 flex-1 overflow-y-auto whitespace-pre-wrap break-words rounded-lg border border-slate-200 bg-slate-50 p-3 font-mono text-xs leading-relaxed text-slate-700 lg:max-h-none"
      >
        {text}
      </pre>
    </div>
  );
}

// 차이 토큰: <split> 은 말풍선 경계선으로
function DiffText({ tokens }: { tokens: DiffToken[] }) {
  return (
    <>
      {tokens.map((t, i) =>
        t.text === "<split>" ? (
          <span
            key={i}
            className="my-1.5 block border-t border-dashed border-slate-300"
          />
        ) : t.kind === "del" ? (
          <del key={i} className="bg-rose-50 text-rose-700">
            {t.text}
          </del>
        ) : t.kind === "ins" ? (
          <ins key={i} className="bg-emerald-50 text-emerald-800 no-underline">
            {t.text}
          </ins>
        ) : (
          <span key={i}>{t.text}</span>
        )
      )}
    </>
  );
}

function Bubbles({ text }: { text: string }) {
  return (
    <div className="flex flex-col">
      {toBubbles(text).map((bubble, i) => (
        <p
          key={i}
          className={
            i > 0 ? "mt-2 border-t border-dashed border-slate-300 pt-2" : ""
          }
        >
          <Masked text={bubble} />
        </p>
      ))}
    </div>
  );
}

function CompareCard({
  title,
  badge,
  onStart,
  children,
}: {
  title: string;
  badge?: ReactNode;
  onStart?: () => void;
  children: ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col rounded-lg border border-slate-200 bg-white">
      <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-3 py-1.5">
        <span className="flex min-w-0 items-center gap-1.5 text-xs font-semibold text-slate-500">
          {title}
          {badge}
        </span>
        <button
          type="button"
          disabled={!onStart}
          onClick={onStart}
          className="shrink-0 rounded-md border border-slate-300 px-2.5 py-0.5 text-xs text-slate-600 hover:border-[#0F55A7] hover:text-[#0F55A7] disabled:opacity-40"
        >
          이걸로 시작
        </button>
      </div>
      <div className="whitespace-pre-wrap break-words px-3 py-2.5 text-sm leading-relaxed text-slate-700">
        {children}
      </div>
    </div>
  );
}

/** AI 추천 vs 코치 최종: 나란히, 차이 보기(단어 단위). 추천이 없거나 안 불러온 턴은 그 사실을 먼저 보인다. */
export function ChatCompare({
  detail,
  diffOn,
  onDiff,
  onStart,
}: {
  detail: AdminAiDatasetDetail;
  diffOn: boolean;
  onDiff: (on: boolean) => void;
  onStart: (text: string) => void;
}) {
  const ai = detail.aiOutput?.text ?? "";
  const coach = detail.coachOutput?.text ?? "";
  const diff = diffOn && ai && coach ? diffTokens(ai, coach) : null;
  const bubbleCount = coach ? toBubbles(coach).length : 0;

  return (
    <div className="flex flex-col gap-2">
      {ai && coach && (
        <label className="flex w-fit cursor-pointer items-center gap-2 text-xs text-slate-500">
          <input
            type="checkbox"
            className="h-3.5 w-3.5 accent-[#0F55A7]"
            checked={diffOn}
            onChange={(e) => onDiff(e.target.checked)}
          />
          차이 보기
          {diffOn && !diff && (
            <span className="text-amber-700">
              글이 길어 차이를 계산하지 않았어요
            </span>
          )}
          {diff && (
            <span>
              · AI 대비{" "}
              <del className="bg-rose-50 px-1 text-rose-700">삭제</del>{" "}
              <ins className="bg-emerald-50 px-1 text-emerald-800 no-underline">
                추가
              </ins>
            </span>
          )}
        </label>
      )}
      <div className="grid grid-cols-1 gap-2 xl:grid-cols-2">
        <CompareCard
          title="AI 추천"
          badge={
            ai && detail.label === "unused" ? (
              <Badge tone="warn">불러오지 않음 · DPO 신호</Badge>
            ) : undefined
          }
          onStart={ai ? () => onStart(ai) : undefined}
        >
          {!ai ? (
            <span className="text-slate-400">
              이 턴엔 AI 추천이 없었어요 · SFT 전용
            </span>
          ) : diff ? (
            <DiffText tokens={diff.ai} />
          ) : (
            <Masked text={ai} />
          )}
        </CompareCard>
        <CompareCard
          title={`코치 최종${bubbleCount > 1 ? ` · 말풍선 ${bubbleCount}` : ""}`}
          onStart={coach ? () => onStart(coach) : undefined}
        >
          {!coach ? (
            <span className="text-slate-400">(비어 있음)</span>
          ) : diff ? (
            <DiffText tokens={diff.coach} />
          ) : (
            <Bubbles text={coach} />
          )}
        </CompareCard>
      </div>
    </div>
  );
}

/**
 * 학습 정답 말풍선 편집기: 말풍선마다 칸, 추가·삭제·순서 바꾸기. 저장하면 <split> 으로 이어진다.
 * value 가 비면 코치 값이 정답이다 (placeholder 로 보인다).
 */
export function BubbleEditor({
  value,
  fallback,
  onChange,
}: {
  value: string;
  fallback: string;
  onChange: (next: string) => void;
}) {
  if (!value) {
    return (
      <div className="flex flex-col gap-2 px-3 pb-3 pt-1.5">
        <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-400">
          {fallback ? <Bubbles text={fallback} /> : "정답이 비어 있어요"}
        </div>
        <div className="flex gap-3 text-xs">
          <button
            type="button"
            disabled={!fallback}
            onClick={() => onChange(fallback)}
            className="font-semibold text-[#0F55A7] hover:underline disabled:opacity-40"
          >
            코치 값으로 고치기 시작
          </button>
          <button
            type="button"
            onClick={() => onChange(fromBubbles([" "]))}
            className="font-semibold text-[#0F55A7] hover:underline"
          >
            새로 쓰기
          </button>
        </div>
      </div>
    );
  }

  const bubbles = toBubbles(value);
  const set = (next: string[]) => onChange(fromBubbles(next));
  const move = (i: number, d: -1 | 1) => {
    const next = [...bubbles];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    set(next);
  };

  return (
    <div className="flex flex-col gap-1.5 px-3 pb-3 pt-1.5">
      {bubbles.map((bubble, i) => (
        <div
          key={i}
          className="grid grid-cols-[1.25rem_minmax(0,1fr)_1.25rem] items-start gap-1.5"
        >
          <div className="flex flex-col pt-1 text-[11px] leading-none text-slate-400">
            <button
              type="button"
              aria-label={`${i + 1}번 말풍선 위로`}
              disabled={i === 0}
              onClick={() => move(i, -1)}
              className="hover:text-[#0F55A7] disabled:opacity-30"
            >
              ▲
            </button>
            <button
              type="button"
              aria-label={`${i + 1}번 말풍선 아래로`}
              disabled={i === bubbles.length - 1}
              onClick={() => move(i, 1)}
              className="mt-1 hover:text-[#0F55A7] disabled:opacity-30"
            >
              ▼
            </button>
          </div>
          <textarea
            aria-label={`학습 정답 ${i + 1}번 말풍선`}
            rows={Math.min(8, Math.max(2, Math.ceil(bubble.length / 40)))}
            className="w-full resize-y rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm leading-relaxed text-slate-900 focus:border-[#0F55A7] focus:bg-white focus:outline-none"
            value={bubble.trim() === "" && bubble !== "" ? "" : bubble}
            onChange={(e) =>
              // 칸을 다 지워도 말풍선은 남긴다 (빈 말풍선은 저장할 때 빠진다)
              set(bubbles.map((b, j) => (j === i ? e.target.value || " " : b)))
            }
          />
          <button
            type="button"
            aria-label={`${i + 1}번 말풍선 삭제`}
            onClick={() => set(bubbles.filter((_, j) => j !== i))}
            className="pt-2 text-sm text-slate-400 hover:text-rose-600"
          >
            ✕
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => set([...bubbles, " "])}
        className="ml-7 w-fit text-xs font-semibold text-[#0F55A7] hover:underline"
      >
        + 말풍선 추가
      </button>
    </div>
  );
}

/** SFT 샘플 요약: user 구성(에이전트 컨텍스트·대화)과 assistant 말풍선 수를 크기와 함께. 길면 경고 */
export function ChatSampleSummary({
  detail,
  answer,
}: {
  detail: AdminAiDatasetDetail;
  answer: string;
}) {
  const size = chatInputSize(detail.input);
  const total = size.contextChars + size.dialogueChars;
  const long = total > CHAT_INPUT_WARN_CHARS;
  const pct = (n: number) =>
    `${Math.max(2, Math.round((n / Math.max(total, 1)) * 100))}%`;
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-slate-200 p-3 text-sm">
      <div className="grid grid-cols-[5.5rem_minmax(0,1fr)] gap-2">
        <span className="font-mono text-xs text-slate-500">user</span>
        <div className="flex flex-col gap-1">
          <span className="text-slate-700">
            {size.contextCount > 0 &&
              `[에이전트 컨텍스트] ${size.contextCount}건 + `}
            [대화] {size.dialogueCount}줄
            <span className={`ml-2 text-xs text-slate-400 ${NUMERIC}`}>
              {size.contextChars > 0 &&
                `${size.contextChars.toLocaleString()}자 + `}
              {size.dialogueChars.toLocaleString()}자
            </span>
          </span>
          <div className="flex h-1.5 overflow-hidden rounded-full bg-slate-100">
            {size.contextChars > 0 && (
              <i
                className={long ? "bg-amber-500" : "bg-[#0F55A7]"}
                style={{ width: pct(size.contextChars) }}
              />
            )}
            <i
              className="bg-slate-400"
              style={{ width: pct(size.dialogueChars) }}
            />
          </div>
          {long && (
            <span className="text-xs text-amber-700">
              입력이 길어요 ({total.toLocaleString()}자). 컨텍스트가 학습 길이
              한도를 넘지 않는지 확인하세요.
            </span>
          )}
        </div>
        <span className="font-mono text-xs text-slate-500">assistant</span>
        <span className="text-slate-700">
          {answer ? (
            <>
              말풍선 {toBubbles(answer).length}개
              <span className={`ml-2 text-xs text-slate-400 ${NUMERIC}`}>
                {answer.split(CHAT_SPLIT).join("").length.toLocaleString()}자
              </span>
            </>
          ) : (
            <span className="text-slate-400">정답 없음 · 샘플 안 만들어짐</span>
          )}
        </span>
      </div>
    </div>
  );
}
